import { Project, projects, ProjectTag } from '@/data/projects'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { CaseStudies } from '@/components/CaseStudies'
import { useLuxeHero, useSectionSequence, useLuxeGrid } from '@/hooks'
import { Terminal, Briefcase, Code2, ExternalLink } from 'lucide-react'

export function Portfolio() {
  // Hero entrance
  const heroRef = useLuxeHero<HTMLElement>(0.3)

  // Work section: sequence
  const workRef = useSectionSequence<HTMLElement>([
    { selector: 'h2', direction: 'left', delay: 0 },
    { selector: '.project-card', direction: 'bottom', stagger: 0.1, delay: 0.15 },
  ])

  // Personal section: sequence from right
  const personalRef = useSectionSequence<HTMLElement>([
    { selector: 'h2', direction: 'right', delay: 0 },
    { selector: '.project-card', direction: 'bottom', stagger: 0.1, delay: 0.15 },
  ])

  // Fugue Lab: grid stagger
  const fugueLabRef = useLuxeGrid<HTMLElement>('.project-card', {
    stagger: 0.12,
    blur: 6,
  })

  // The three flagship AI systems are featured up top via <CaseStudies />,
  // so exclude them from the "Independent Projects" grid to avoid duplication.
  const caseStudySlugs = ['fugue-lab-leadgen-agents', 'finboard-doctrine-agent', 'full-score-quality-engine']
  const workProjects = projects.filter((p) => p.tags.includes(ProjectTag.RebuildManufacturing))
  const personalProjects = projects.filter(
    (p) => p.tags.includes(ProjectTag.Personal) && !caseStudySlugs.includes(p.slug)
  )
  const featuredFugueLabSlugs = [
    'sierra-canyon-photo',
    'classically-minded',
    'chroma-music-festival',
    'susan-bengtson-price',
  ]
  const fugueLabProjects = projects.filter(
    (p) => p.tags.includes(ProjectTag.FugueLab) && featuredFugueLabSlugs.includes(p.slug)
  )

  return (
    <>
      <SEO
        title="Portfolio"
        description="Production AI systems — a ~15-agent LLM fleet, a 135-tool operations agent, and an automated website-evaluation engine — plus AI infrastructure and backend work at Re:Build Manufacturing."
        url="https://software.garrettjohnlaw.com/portfolio"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-20 md:py-28">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <p className="text-primary mb-4 font-mono text-lg font-medium tracking-wide">&lt;portfolio /&gt;</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">AI Systems &amp; Engineering</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Production AI systems first, then the infrastructure and backend work behind them. The numbers here trace to
            the source and runtime config behind them.
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Flagship AI systems */}
      <CaseStudies />

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Work Projects */}
      <section ref={workRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex items-center gap-3">
            <div className="gradient-cyber flex h-10 w-10 items-center justify-center rounded-lg">
              <Terminal className="text-primary-foreground h-5 w-5" />
            </div>
            <h2 className="text-fluid-h2 text-foreground">Re:Build Manufacturing</h2>
          </div>

          <div className="grid gap-6">
            {workProjects.map((project) => (
              <ProjectCard key={project.slug} project={project} />
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Personal Projects */}
      {personalProjects.length > 0 && (
        <>
          <section ref={personalRef} className="px-4 py-16 md:py-24">
            <div className="mx-auto max-w-5xl">
              <div className="mb-8 flex items-center gap-3">
                <div className="gradient-cyber flex h-10 w-10 items-center justify-center rounded-lg">
                  <Code2 className="text-primary-foreground h-5 w-5" />
                </div>
                <h2 className="text-fluid-h2 text-foreground">Independent Projects</h2>
              </div>

              <div className="grid gap-6">
                {personalProjects.map((project) => (
                  <ProjectCard key={project.slug} project={project} />
                ))}
              </div>
            </div>
          </section>

          {/* Section Divider */}
          <div className="section-divider mx-auto max-w-4xl" />
        </>
      )}

      {/* Fugue Lab Projects */}
      <section ref={fugueLabRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-4 flex items-center gap-3">
            <div className="gradient-cyber flex h-10 w-10 items-center justify-center rounded-lg">
              <Briefcase className="text-primary-foreground h-5 w-5" />
            </div>
            <h2 className="text-fluid-h2 text-foreground">Fugue Lab</h2>
          </div>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            Freelance software work for musicians and performing arts professionals. Built under my creative studio,
            Fugue Lab.
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            {fugueLabProjects.map((project) => (
              <ProjectCard key={project.slug} project={project} compact />
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

function ProjectCard({ project, compact = false }: { project: Project; compact?: boolean }) {
  return (
    <GlassCard hover className="project-card">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <h3 className={`text-foreground font-semibold ${compact ? 'text-lg' : 'text-xl'}`}>
            {project.url ? (
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary inline-flex items-center gap-2 transition-colors"
              >
                {project.title}
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : (
              project.title
            )}
          </h3>
          {project.tags && !compact && (
            <div className="flex flex-wrap gap-2">
              {project.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="bg-primary/10 text-primary border-primary/20 rounded border px-2 py-0.5 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <p className={`text-muted-foreground ${compact ? 'text-sm' : ''}`}>{project.description}</p>

        <p className="text-primary font-mono text-xs">{project.tech.join(' · ')}</p>
      </div>
    </GlassCard>
  )
}
